import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'guest' | 'admin'
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  initialized: boolean
  loading: boolean
  error: string | null
}

const isBrowser = () => typeof window !== 'undefined'

const getFromStorage = (key: string): string | null => {
  if (!isBrowser()) {
    return null
  }

  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const setInStorage = (key: string, value: string) => {
  if (!isBrowser()) {
    return
  }

  try {
    localStorage.setItem(key, value)
  } catch {
    // Ignore storage failures (private mode/quota issues)
  }
}

const removeFromStorage = (key: string) => {
  if (!isBrowser()) {
    return
  }

  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore storage failures (private mode/quota issues)
  }
}

const getStoredUser = (): User | null => {
  try {
    const rawUser = getFromStorage('user')
    return rawUser ? JSON.parse(rawUser) : null
  } catch {
    return null
  }
}

const getStoredToken = (): string | null => getFromStorage('token')

const initialState: AuthState = {
  user: getStoredUser(),
  token: getStoredToken(),
  isAuthenticated: !!getStoredToken(),
  initialized: false,
  loading: false,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true
      state.error = null
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.loading = false
      state.initialized = true
      state.isAuthenticated = true
      state.user = action.payload.user
      state.token = action.payload.token
      setInStorage('token', action.payload.token)
      setInStorage('user', JSON.stringify(action.payload.user))
    },
    login: (state, action: PayloadAction<User>) => {
      state.loading = false
      state.initialized = true
      state.isAuthenticated = true
      state.user = action.payload
      state.token = `token_${action.payload.id}_${Date.now()}`
      setInStorage('token', state.token)
      setInStorage('user', JSON.stringify(action.payload))
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.initialized = true
      state.error = action.payload
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.initialized = true
      state.isAuthenticated = false
      removeFromStorage('token')
      removeFromStorage('user')
    },
    setSession: (state, action: PayloadAction<{ user: User; token: string } | null>) => {
      state.loading = false
      state.initialized = true

      if (action.payload) {
        state.isAuthenticated = true
        state.user = action.payload.user
        state.token = action.payload.token
        setInStorage('token', action.payload.token)
        setInStorage('user', JSON.stringify(action.payload.user))
        return
      }

      state.user = null
      state.token = null
      state.isAuthenticated = false
      removeFromStorage('token')
      removeFromStorage('user')
    },
    authResolved: (state) => {
      state.initialized = true
    },
    clearError: (state) => {
      state.error = null
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
        setInStorage('user', JSON.stringify(state.user))
      }
    },
  },
})

export const { loginStart, loginSuccess, loginFailure, login, logout, setSession, authResolved, clearError, updateUser } = authSlice.actions
export default authSlice.reducer